import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatUnit } from "@/i18n/units";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Heart, ChefHat, Plus, Loader2, Flame, Lightbulb,
  Trash2, ArrowLeft, Check, X, Clock, Video, Image, Pencil
} from "lucide-react";
import TipCard from "@/components/TipCard";
import { LIKED_TIPS_KEY, normalizeLikedTip, tipKeyOf } from "@/lib/likedTips";

const UNITS = ["г", "кг", "мл", "л", "шт", "ст.л", "ч.л", "склянка", "пучок", "зубчик", "головка", "щіпка"];

const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"];

const EMPTY_STEP = () => ({ text: "", photo: null });
const EMPTY_RECIPE = () => ({
  title: "", description: "", cook_time: "", meal_type: "",
  ingredients: [{ name: "", amount: "", unit: "г" }],
  steps: [EMPTY_STEP()],
  video_url: "",
});

export default function MyDishesPage() {
  const { api } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const photoInputRefs = useRef({});

  const params = new URLSearchParams(location.search);
  const addToMealType = params.get("addTo");
  const addToPlanId   = params.get("planId");
  const addToDate     = params.get("date");
  const isAddMode     = Boolean(addToMealType && addToPlanId && addToDate);

  const [tab, setTab] = useState(params.get("tab") === "myRecipes" ? "my_recipes" : "favorites");
  const [favorites, setFavorites] = useState([]);
  const [myRecipes, setMyRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingRecipeId, setAddingRecipeId] = useState(null);
  const [addedRecipeId, setAddedRecipeId] = useState(null);
  const [likedTips, setLikedTips] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LIKED_TIPS_KEY) || "[]").map(normalizeLikedTip); }
    catch { return []; }
  });
  const [expandedTipIdx, setExpandedTipIdx] = useState(null);

  const RECIPES_PER_PAGE = 20;
  const [recipesPage, setRecipesPage] = useState(1);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRecipe, setNewRecipe] = useState(EMPTY_RECIPE);
  const [savingRecipe, setSavingRecipe] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [editRecipeId, setEditRecipeId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [videoUrlError, setVideoUrlError] = useState("");

  // Ingredient autocomplete
  const [activeIngIdx, setActiveIngIdx] = useState(null);
  const [ingSuggestions, setIngSuggestions] = useState([]);
  const [ingSearch, setIngSearch] = useState("");
  const ingSuggestRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [favRes, recRes] = await Promise.all([
        api().get("/favorites"),
        api().get("/recipes?meal_type="),
      ]);
      setFavorites(favRes.data);
      setMyRecipes(recRes.data.filter(r => r.is_user_recipe));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (deleteConfirmId) { setDeleteConfirmId(null); return; }
      if (showCreateModal) { setShowCreateModal(false); setSaveError(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteConfirmId, showCreateModal]);

  const removeLikedTip = (tipKey) => {
    setLikedTips(prev => {
      const next = prev.filter(t => tipKeyOf(t) !== tipKey);
      localStorage.setItem(LIKED_TIPS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleAddToPlan = async (recipe) => {
    if (!isAddMode || addingRecipeId) return;
    setAddingRecipeId(recipe.id);
    try {
      await api().post("/meal-plans/add-existing-recipe", {
        plan_id: addToPlanId, date: addToDate,
        meal_type: addToMealType, recipe_id: recipe.id,
      });
      setAddedRecipeId(recipe.id);
      setTimeout(() => navigate(-1), 800);
    } catch (err) { console.error(err); }
    setAddingRecipeId(null);
  };

  const openCreateModal = useCallback(() => {
    setNewRecipe(EMPTY_RECIPE());
    setEditRecipeId(null);
    setSaveError(false);
    setShowCreateModal(true);
    setActiveIngIdx(null);
    setIngSearch("");
    setIngSuggestions([]);
  }, []);

  // React to URL params after mount (tab switch + create intent from Quick Actions)
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const tabParam = p.get("tab");
    const createParam = p.get("create") === "1";
    if (tabParam === "myRecipes") setTab("my_recipes");
    else if (tabParam === "favorites") setTab("favorites");
    if (createParam) openCreateModal();
    // strip transient params so they don't reopen modal / re-force tab on re-render or back
    if (tabParam || createParam) {
      p.delete("tab");
      p.delete("create");
      navigate({ search: p.toString() }, { replace: true });
    }
  }, [location.search, openCreateModal, navigate]);

  const openEditModal = (recipe) => {
    setNewRecipe({
      title: recipe.title || "",
      description: recipe.description || "",
      cook_time: recipe.cook_time ? String(recipe.cook_time) : "",
      meal_type: recipe.meal_type || "",
      ingredients: recipe.ingredients?.length
        ? recipe.ingredients.map(i => ({ name: i.name || "", amount: i.amount ? String(i.amount) : "", unit: i.unit || "г" }))
        : [{ name: "", amount: "", unit: "г" }],
      steps: recipe.instructions?.length
        ? recipe.instructions.map(s => typeof s === "object" ? { text: s.text || "", photo: s.photo || null } : { text: s, photo: null })
        : [EMPTY_STEP()],
      video_url: recipe.video_url || "",
    });
    setEditRecipeId(recipe.id);
    setSaveError(false);
    setShowCreateModal(true);
    setActiveIngIdx(null);
    setIngSearch("");
    setIngSuggestions([]);
  };

  // Ingredient autocomplete search
  useEffect(() => {
    if (activeIngIdx === null) { setIngSuggestions([]); return; }
    const query = ingSearch.trim();
    const search = async () => {
      try {
        const res = await api().get(`/products/search?query=${encodeURIComponent(query)}&limit=8`);
        setIngSuggestions(res.data?.results || []);
      } catch { setIngSuggestions([]); }
    };
    const t = setTimeout(search, 150);
    return () => clearTimeout(t);
  }, [ingSearch, activeIngIdx, api]);

  const selectIngSuggestion = (idx, suggestion) => {
    updateIng(idx, "name", suggestion.name);
    setActiveIngIdx(null);
    setIngSuggestions([]);
    setIngSearch("");
  };

  // Ingredient helpers
  const updateIng = (idx, field, val) =>
    setNewRecipe(r => { const a = [...r.ingredients]; a[idx] = { ...a[idx], [field]: val }; return { ...r, ingredients: a }; });
  const addIng = () =>
    setNewRecipe(r => ({ ...r, ingredients: [...r.ingredients, { name: "", amount: "", unit: "г" }] }));
  const removeIng = (idx) =>
    setNewRecipe(r => ({ ...r, ingredients: r.ingredients.filter((_, i) => i !== idx) }));

  // Step helpers
  const updateStep = (idx, field, val) =>
    setNewRecipe(r => { const a = [...r.steps]; a[idx] = { ...a[idx], [field]: val }; return { ...r, steps: a }; });
  const addStep = () =>
    setNewRecipe(r => ({ ...r, steps: [...r.steps, EMPTY_STEP()] }));
  const removeStep = (idx) =>
    setNewRecipe(r => ({ ...r, steps: r.steps.filter((_, i) => i !== idx) }));

  const handleStepPhoto = (idx, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => updateStep(idx, "photo", e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSaveRecipe = async () => {
    if (!newRecipe.title.trim()) return;
    setSavingRecipe(true);
    setSaveError(false);
    try {
      const ingredients = newRecipe.ingredients.filter(i => i.name.trim());
      const instructions = newRecipe.steps.map(s => ({ text: s.text, photo: s.photo || null }));
      const payload = {
        title: newRecipe.title.trim(),
        description: newRecipe.description.trim() || null,
        meal_type: newRecipe.meal_type || null,
        cook_time: newRecipe.cook_time ? parseInt(newRecipe.cook_time) : null,
        ingredients,
        instructions,
        video_url: newRecipe.video_url.trim() || null,
        servings: 2,
        is_user_recipe: true,
      };
      if (editRecipeId) {
        await api().put(`/recipes/${editRecipeId}`, payload);
      } else {
        await api().post("/recipes", payload);
      }
      setShowCreateModal(false);
      setEditRecipeId(null);
      await fetchData();
      setTab("my_recipes");
    } catch (err) {
      console.error(err);
      setSaveError(true);
    }
    setSavingRecipe(false);
  };

  const handleDeleteRecipe = async (recipeId) => {
    try {
      await api().delete(`/recipes/${recipeId}`);
      await fetchData();
    } catch (err) { console.error(err); }
    setDeleteConfirmId(null);
  };

  const allDishes = tab === "favorites" ? favorites : myRecipes;
  const dishes = tab === "my_recipes"
    ? allDishes.slice(0, recipesPage * RECIPES_PER_PAGE)
    : allDishes;
  const hasMore = tab === "my_recipes" && allDishes.length > recipesPage * RECIPES_PER_PAGE;

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-28" data-testid="my-dishes-page">
      {/* Header */}
      <div className="pl-[72px] pr-5 pt-6 pb-4">
        {isAddMode ? (
          <>
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#6B705C] font-body text-sm mb-3">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {t("back") || "Назад"}
            </button>
            <h1 className="font-heading text-2xl text-[#3D4035]">{t("selectDishForPlan") || "Виберіть страву"}</h1>
            <p className="font-body text-sm text-[#A5A58D] mt-1">{t(addToMealType) || addToMealType}</p>
          </>
        ) : (
          <h1 className="font-heading text-3xl text-[#3D4035]">{t("myDishesTitle")}</h1>
        )}
      </div>

      {/* Tabs */}
      <div className="px-5 mb-6">
        <div className="flex bg-[#F2E9E4] rounded-full p-1">
          {[
            { key: "favorites", icon: Heart, label: t("favorites"), count: favorites.length },
            { key: "my_recipes", icon: ChefHat, label: t("myRecipes"), count: myRecipes.length },
            { key: "liked_tips", icon: Lightbulb, label: t("likedTips") || "Tips", count: likedTips.length, activeColor: "text-[#D4856B]" },
          ].map(({ key, icon: Icon, label, count, activeColor }) => (
            <button key={key} data-testid={`dishes-tab-${key.replace("_","-")}`}
              onClick={() => setTab(key)}
              className={`flex-1 py-2.5 rounded-full font-body text-sm font-semibold transition-all ${
                tab === key ? `bg-white shadow-sm ${activeColor || "text-[#7B9E7F]"}` : "text-[#6B705C]"
              }`}>
              <Icon className="w-4 h-4 inline-block mr-1.5" strokeWidth={1.5} />
              {label} ({count})
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-5">
        {tab === "liked_tips" ? (
          likedTips.length === 0 ? (
            <div className="text-center py-16">
              <Lightbulb className="w-12 h-12 text-[#A5A58D] mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-body text-[#6B705C]">{t("noDishes")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {likedTips.map((tip, idx) => (
                <TipCard
                  key={idx}
                  tip={tip}
                  idx={idx}
                  expanded={expandedTipIdx === idx}
                  onExpand={tip.message ? (i) => setExpandedTipIdx(expandedTipIdx === i ? null : i) : undefined}
                  onRemove={() => removeLikedTip(tipKeyOf(tip))}
                />
              ))}
            </div>
          )
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-[#7B9E7F] animate-spin" />
          </div>
        ) : dishes.length === 0 ? (
          <div className="text-center py-16">
            {tab === "my_recipes" ? (
              <>
                <ChefHat className="w-12 h-12 text-[#A5A58D] mx-auto mb-3" strokeWidth={1.5} />
                <p className="font-body text-[#6B705C] mb-4">{t("noMyRecipes") || "Поки немає власних рецептів"}</p>
                <button onClick={openCreateModal}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#7B9E7F] text-white rounded-full font-body text-sm font-semibold hover:bg-[#6B8E6F] transition-colors">
                  <Plus className="w-4 h-4" strokeWidth={2} /> {t("createRecipe") || "Створити рецепт"}
                </button>
              </>
            ) : (
              <>
                <Heart className="w-12 h-12 text-[#A5A58D] mx-auto mb-3" strokeWidth={1.5} />
                <p className="font-body text-[#6B705C]">{t("noDishes")}</p>
              </>
            )}
          </div>
        ) : (
          <>
          <div className="space-y-3">
            {dishes.map(recipe => {
              const isAdded = addedRecipeId === recipe.id;
              const isAdding = addingRecipeId === recipe.id;
              return (
                <button key={recipe.id} data-testid={`dish-card-${recipe.id}`}
                  onClick={() => isAddMode ? handleAddToPlan(recipe) : navigate(`/recipe/${recipe.id}`)}
                  disabled={isAdding || isAdded}
                  className={`w-full bg-white rounded-2xl p-4 shadow-sm border transition-all text-left flex items-center gap-4 ${isAdded ? "border-[#7B9E7F] bg-[#7B9E7F]/5" : "border-black/5 hover:shadow-md"}`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${isAdded ? "bg-[#7B9E7F]" : "bg-[#F2E9E4]"}`}>
                    {isAdding ? <Loader2 className="w-6 h-6 text-[#7B9E7F] animate-spin" strokeWidth={1.5} />
                      : isAdded ? <Check className="w-6 h-6 text-white" strokeWidth={2} />
                      : <ChefHat className="w-6 h-6 text-[#7B9E7F]" strokeWidth={1.5} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-body font-semibold text-[#3D4035] truncate">{recipe.title}</p>
                    {recipe.description && <p className="font-body text-sm text-[#A5A58D] truncate">{recipe.description}</p>}
                    <div className="flex items-center gap-3 mt-1">
                      {recipe.meal_type && <span className="font-body text-xs text-[#A5A58D]">{t(recipe.meal_type)}</span>}
                      {recipe.cook_time && <span className="flex items-center gap-1 text-xs font-body text-[#A5A58D]"><Clock className="w-3 h-3" strokeWidth={1.5} /> {recipe.cook_time} {t("min")}</span>}
                      {recipe.calories && <span className="flex items-center gap-1 text-xs font-body text-[#A5A58D]"><Flame className="w-3 h-3" strokeWidth={1.5} /> {recipe.calories}</span>}
                    </div>
                  </div>
                  {isAddMode && !isAdded && <Plus className="w-5 h-5 text-[#7B9E7F] shrink-0" strokeWidth={1.5} />}
                  {!isAddMode && tab === "my_recipes" && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={e => { e.stopPropagation(); openEditModal(recipe); }}
                        className="w-8 h-8 rounded-xl bg-[#F2E9E4] flex items-center justify-center hover:bg-[#7B9E7F]/10 transition-colors">
                        <Pencil className="w-3.5 h-3.5 text-[#6B705C]" strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteConfirmId(recipe.id); }}
                        className="w-8 h-8 rounded-xl bg-[#FFF0ED] flex items-center justify-center hover:bg-[#BC4749]/15 transition-colors">
                        <Trash2 className="w-3.5 h-3.5 text-[#D4856B]" strokeWidth={1.5} />
                      </button>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {hasMore && (
            <button
              onClick={() => setRecipesPage(p => p + 1)}
              className="w-full mt-4 py-3 bg-[#F2E9E4] text-[#7B9E7F] rounded-2xl font-body font-semibold text-sm hover:bg-[#E8E5E0] transition-colors">
              {t("loadMore") || "Завантажити ще"} ({allDishes.length - dishes.length})
            </button>
          )}
          </>
        )}
      </div>

      {/* FAB — bottom-right, above BottomNav, only on my_recipes tab */}
      {tab === "my_recipes" && !showCreateModal && (
        <button onClick={openCreateModal}
          className="fixed bottom-24 right-5 z-[9000] w-14 h-14 rounded-full bg-[#7B9E7F] text-white shadow-lg hover:bg-[#6B8E6F] transition-all flex items-center justify-center active:scale-95">
          <Plus className="w-6 h-6" strokeWidth={2.5} />
        </button>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[10002] bg-black/50 flex items-center justify-center px-5">
          <div className="w-full max-w-sm bg-white rounded-3xl p-7 shadow-2xl text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#BC4749]/10 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-7 h-7 text-[#BC4749]" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-xl text-[#3D4035] mb-2">{t("deleteRecipeTitle")}</h3>
            <p className="font-body text-sm text-[#6B705C] mb-6">{t("actionCannotUndo")}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-3 rounded-2xl bg-[#EBE9E4] font-body font-semibold text-[#3D4035] hover:bg-[#E0DDD7] transition-colors">
                {t("cancel")}
              </button>
              <button onClick={() => handleDeleteRecipe(deleteConfirmId)}
                className="flex-1 py-3 rounded-2xl bg-[#BC4749] font-body font-semibold text-white hover:bg-[#A33A3C] transition-colors">
                {t("delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Recipe Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-end">
          <div className="w-full max-w-[480px] mx-auto bg-[#FAFAF8] rounded-t-3xl flex flex-col max-h-[95vh]">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 shrink-0 border-b border-black/5">
              <h3 className="font-heading text-xl text-[#3D4035]">{editRecipeId ? (t("editRecipe") || "Редагувати рецепт") : (t("createRecipe") || "Новий рецепт")}</h3>
              <button onClick={() => { setShowCreateModal(false); setSaveError(false); }}
                className="w-9 h-9 rounded-full bg-[#EBE9E4] flex items-center justify-center">
                <X className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto px-6 py-5 space-y-5 flex-1">

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-[#6B705C] mb-1.5">{t("nameField")} *</label>
                <input type="text" value={newRecipe.title}
                  onChange={e => setNewRecipe(r => ({ ...r, title: e.target.value }))}
                  placeholder={t("recipeNamePlaceholder")}
                  autoFocus
                  className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none" />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-[#6B705C] mb-1.5">{t("description")}</label>
                <input type="text" value={newRecipe.description}
                  onChange={e => setNewRecipe(r => ({ ...r, description: e.target.value }))}
                  placeholder={t("optional")}
                  className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none" />
              </div>

              {/* Meal type + cook time */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-[#6B705C] mb-1.5">{t("mealType")}</label>
                  <select value={newRecipe.meal_type}
                    onChange={e => setNewRecipe(r => ({ ...r, meal_type: e.target.value }))}
                    className="w-full bg-white ring-1 ring-[#E8E5E0] rounded-xl h-12 px-3 font-body text-sm text-[#3D4035] outline-none">
                    <option value="">{t("notSpecified")}</option>
                    {MEAL_TYPES.map(m => <option key={m} value={m}>{t(m)}</option>)}
                  </select>
                </div>
                <div className="w-28">
                  <label className="block text-xs font-semibold text-[#6B705C] mb-1.5">{t("cookTimeMinLabel")}</label>
                  <input type="number" value={newRecipe.cook_time}
                    onChange={e => setNewRecipe(r => ({ ...r, cook_time: e.target.value }))}
                    placeholder="30"
                    className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-sm text-[#3D4035] outline-none" />
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <label className="block text-xs font-semibold text-[#6B705C] mb-2">{t("ingredients")}</label>
                <div className="space-y-2">
                  {newRecipe.ingredients.map((ing, idx) => (
                    <div key={idx} className="flex gap-1.5 items-start">
                      {/* Name with autocomplete */}
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={activeIngIdx === idx ? ingSearch : ing.name}
                          onChange={e => {
                            setActiveIngIdx(idx);
                            setIngSearch(e.target.value);
                            updateIng(idx, "name", e.target.value);
                          }}
                          onFocus={() => {
                            setActiveIngIdx(idx);
                            setIngSearch(ing.name);
                          }}
                          onBlur={() => setTimeout(() => {
                            setActiveIngIdx(null);
                            setIngSuggestions([]);
                          }, 150)}
                          placeholder={t("nameField")}
                          className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-11 px-3 font-body text-sm text-[#3D4035] outline-none"
                        />
                        {activeIngIdx === idx && ingSuggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl shadow-xl border border-black/5 z-50 max-h-44 overflow-y-auto">
                            {ingSuggestions.map((s, si) => (
                              <button
                                key={si}
                                onMouseDown={() => selectIngSuggestion(idx, s)}
                                className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-[#F2E9E4] transition-colors text-left first:rounded-t-2xl last:rounded-b-2xl"
                              >
                                <span className="text-lg leading-none">{s.icon || "🥗"}</span>
                                <span className="font-body text-sm text-[#3D4035]">{s.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <input type="text" value={ing.amount}
                        onChange={e => updateIng(idx, "amount", e.target.value)}
                        placeholder="100"
                        className="w-16 bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-11 px-2 font-body text-sm text-[#3D4035] outline-none text-center" />
                      <select value={ing.unit}
                        onChange={e => updateIng(idx, "unit", e.target.value)}
                        className="w-20 bg-white ring-1 ring-[#E8E5E0] rounded-xl h-11 px-1 font-body text-sm text-[#3D4035] outline-none">
                        {UNITS.map(u => <option key={u} value={u}>{formatUnit(u, lang)}</option>)}
                      </select>
                      {newRecipe.ingredients.length > 1 && (
                        <button onClick={() => removeIng(idx)}
                          className="w-9 h-9 mt-1 rounded-xl bg-[#FFF0ED] text-[#D4856B] flex items-center justify-center shrink-0 hover:bg-[#D4856B]/20 transition-colors">
                          <X className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button onClick={addIng}
                  className="flex items-center gap-2 text-sm text-[#7B9E7F] font-semibold mt-3 py-1">
                  <Plus className="w-4 h-4" strokeWidth={2} /> {t("addIngredient")}
                </button>
              </div>

              {/* Steps */}
              <div>
                <label className="block text-xs font-semibold text-[#6B705C] mb-2">{t("cookingSteps")}</label>
                <div className="space-y-3">
                  {newRecipe.steps.map((step, idx) => (
                    <div key={idx} className="bg-white rounded-2xl border border-[#E8E5E0] p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-6 h-6 rounded-full bg-[#7B9E7F] text-white text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                        <span className="text-xs font-semibold text-[#6B705C] flex-1">{t("step")} {idx + 1}</span>
                        {newRecipe.steps.length > 1 && (
                          <button onClick={() => removeStep(idx)}
                            className="w-7 h-7 rounded-lg bg-[#FFF0ED] text-[#D4856B] flex items-center justify-center hover:bg-[#D4856B]/20 transition-colors">
                            <X className="w-3 h-3" strokeWidth={2} />
                          </button>
                        )}
                      </div>
                      <textarea value={step.text}
                        onChange={e => updateStep(idx, "text", e.target.value)}
                        placeholder={`${t("describeStep")} ${idx + 1}...`}
                        rows={2}
                        className="w-full bg-[#FAFAF8] ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl px-3 py-2.5 font-body text-sm text-[#3D4035] outline-none resize-none" />

                      {/* Step photo */}
                      <div className="mt-2">
                        {step.photo ? (
                          <div className="relative">
                            <img src={step.photo} alt={`${t("step")} ${idx + 1}`}
                              className="w-full h-32 object-cover rounded-xl" />
                            <button onClick={() => updateStep(idx, "photo", null)}
                              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center">
                              <X className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <input type="file" accept="image/*"
                              ref={el => photoInputRefs.current[idx] = el}
                              onChange={e => handleStepPhoto(idx, e.target.files[0])}
                              className="hidden" />
                            <button onClick={() => photoInputRefs.current[idx]?.click()}
                              className="flex items-center gap-2 text-xs text-[#A5A58D] hover:text-[#7B9E7F] transition-colors py-1">
                              <Image className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("addStepPhoto")}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={addStep}
                  className="flex items-center gap-2 text-sm text-[#7B9E7F] font-semibold mt-3 py-1">
                  <Plus className="w-4 h-4" strokeWidth={2} /> {t("addStep")}
                </button>  
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-xs font-semibold text-[#6B705C] mb-1.5 flex items-center gap-1.5">
                  <Video className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("recipeVideo")}
                </label>
                <input type="url" value={newRecipe.video_url}
                  onChange={e => { setNewRecipe(r => ({ ...r, video_url: e.target.value })); setVideoUrlError(""); }}
                  onBlur={e => {
                    const v = e.target.value.trim();
                    if (v && !/^https?:\/\/.+\..+/.test(v)) setVideoUrlError("Введіть коректне посилання (https://...)");
                    else setVideoUrlError("");
                  }}
                  placeholder={t("videoUrlPlaceholder")}
                  className={`w-full bg-white ring-1 rounded-xl h-12 px-4 font-body text-sm text-[#3D4035] outline-none ${videoUrlError ? "ring-[#BC4749]" : "ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50"}`} />
                {videoUrlError && <p className="text-xs text-[#BC4749] mt-1">{videoUrlError}</p>}
              </div>

              {/* Save error */}
              {saveError && (
                <div data-testid="recipe-save-error" className="text-[#BC4749] bg-[#BC4749]/10 rounded-xl px-4 py-3 text-sm font-body">
                  {t("recipeSaveFailed")}
                </div>
              )}

              {/* Save */}
              <button onClick={handleSaveRecipe}
                disabled={!newRecipe.title.trim() || savingRecipe}
                className="w-full bg-[#7B9E7F] text-white rounded-2xl py-4 font-body font-bold text-base hover:bg-[#6B8E6F] transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-[#7B9E7F]/20">
                {savingRecipe ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" strokeWidth={2} />}
                {t("saveRecipe") || "Зберегти рецепт"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
